<script setup>
import { computed } from 'vue';

// Pure-SVG path renderer — NO Chart.js para que el footprint quede
// reservado a la PageviewsPanel chart. El componente recibe
// `points: [{ ts, value }]` y emite un `<svg role="img">` con
// path lineal.

const props = defineProps({
  points: {
    type: Array,
    required: true,
    validator: (arr) => Array.isArray(arr) && arr.every((p) =>
      p && typeof p.value === 'number' && (typeof p.ts === 'string' || p.ts instanceof Date)),
  },
  width: { type: Number, default: 120 },
  height: { type: Number, default: 32 },
  // Aria label override; si no, se computa del array.
  ariaLabel: { type: String, default: '' },
});

const hasData = computed(() => props.points.length > 0);

const stats = computed(() => {
  if (!hasData.value) return { min: 0, max: 1, sum: 0, peak: null };
  let min = Infinity, max = -Infinity, sum = 0, peakIdx = 0;
  for (let i = 0; i < props.points.length; i++) {
    const v = props.points[i].value;
    if (v < min) min = v;
    if (v > max) { max = v; peakIdx = i; }
    sum += v;
  }
  return { min, max, sum, peak: { index: peakIdx, value: max, ts: props.points[peakIdx].ts } };
});

const pathD = computed(() => {
  if (!hasData.value) return '';
  const pts = props.points;
  const n = pts.length;
  const { min, max } = stats.value;
  const range = max - min || 1;
  // Mapeamos cada punto a (x, y) en el viewBox.
  const dx = n > 1 ? props.width / (n - 1) : 0;
  let out = '';
  for (let i = 0; i < n; i++) {
    const x = i * dx;
    const norm = (pts[i].value - min) / range;
    // Y invertido: SVG 0 está arriba, queremos value alto = arriba.
    const y = props.height - norm * props.height;
    out += `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)} `;
  }
  return out.trim();
});

const computedAriaLabel = computed(() => {
  if (props.ariaLabel) return props.ariaLabel;
  if (!hasData.value) return 'No data';
  const first = props.points[0].value;
  const last = props.points[props.points.length - 1].value;
  const direction = last > first ? 'Up' : last < first ? 'Down' : 'Steady';
  const pct = first === 0 ? null : Math.round(((last - first) / first) * 100);
  const peakStr = stats.value.peak
    ? `, peak ${stats.value.peak.value} at ${typeof stats.value.peak.ts === 'string' ? stats.value.peak.ts : stats.value.peak.ts.toISOString()}`
    : '';
  if (pct === null) {
    return `Trend ${direction.toLowerCase()} from ${first} to ${last}${peakStr}`;
  }
  return `Trend ${direction.toLowerCase()} ${Math.abs(pct)}% from ${first} to ${last}${peakStr}`;
});
</script>

<template>
  <svg
    role="img"
    :aria-label="computedAriaLabel"
    :viewBox="`0 0 ${width} ${height}`"
    :width="width"
    :height="height"
    class="sparkline"
    preserveAspectRatio="none"
  >
    <path
      v-if="hasData"
      :d="pathD"
      class="sparkline__path"
      fill="none"
    />
    <text
      v-else
      x="50%"
      y="50%"
      class="sparkline__empty"
      text-anchor="middle"
      dominant-baseline="middle"
    >—</text>
  </svg>
</template>

<style scoped>
.sparkline {
  display: block;
}

.sparkline__path {
  stroke: var(--chart-1);
  stroke-width: var(--bw-thick);
  stroke-linecap: round;
  stroke-linejoin: round;
}

.sparkline__empty {
  fill: var(--fg-secondary);
  font-size: var(--text-xs);
}
</style>
